import React, { useMemo } from "react";
import { useProjectDesignationOptions } from "../../../../../../Hooks/useProjectDesignationOptions.js";

import {
  Button,
  ResponsiveDataTable,
  TextField,
} from "../../../../ui/index.ts";
import type { ResponsiveColumn } from "../../../../ui/responsive-table/types.ts";
import type { ProjectWithAllocationsDerived } from "../../../../../hooks/time-allocation/use-time-allocation-page.ts";
import { getDesignationDisplayLabel } from "../../../../../utils/designation-display.ts";
import { digitsOnlyInput } from "../../../../../utils/helpers.ts";

import styles from "./employee-allocation-table.module.css";

export type EmployeeTableRow = {
  projectId: string;
  id: string;
  employeeId: string;
  name: string;
  designation: string;
  alreadyAllocatedHours: number;
  apiRemainingHours: number;
  assignedHours: number;
  remainingHours: number;
};

/** Whole hours for display (backend minutes ÷ 60 may be fractional). */
function formatHoursWhole(n: number): string {
  return String(Math.round(Number.isFinite(n) ? n : 0));
}

function buildColumns(
  project: ProjectWithAllocationsDerived,
  setAssignedHours: (projectId: string, employeeId: string, hours: number) => void,
  openViewAllocations: (projectId: string, employeeId: string) => void,
): ResponsiveColumn<EmployeeTableRow>[] {
  return [
    {
      id: "name",
      header: "Name",
      accessor: "name",
      sortable: true,
      truncate: true,
    },
    {
      id: "designation",
      header: "Designation",
      sortable: true,
      truncate: true,
      getSortValue: (row) => getDesignationDisplayLabel(row.designation),
      render: (row) => (
        <span className="body-sm">{getDesignationDisplayLabel(row.designation)}</span>
      ),
      getTitleText: (row) => getDesignationDisplayLabel(row.designation),
    },
    {
      id: "alreadyAllocatedHours",
      header: "Already Allocated",
      accessor: "alreadyAllocatedHours",
      sortable: true,
      getSortValue: (row) => Math.round(row.alreadyAllocatedHours),
      nowrap: true,
      render: (row) => (
        <span className="body-sm">{formatHoursWhole(row.alreadyAllocatedHours)}</span>
      ),
    },
    {
      id: "assignedHours",
      header: "Allocate Hours",
      sortable: false,
      render: (row) => (
        <div className={styles.assignedCell}>
          <TextField
            // label="Allocated hours"
            id={`assigned-${project.id}-${row.employeeId}`}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={
              row.assignedHours === 0
                ? ""
                : formatHoursWhole(row.assignedHours)
            }
            onChange={(e) => {
              const cleaned = digitsOnlyInput(e.target.value, 3);
              if (cleaned === "") {
                setAssignedHours(project.id, row.employeeId, 0);
                return;
              }
              const n = Number.parseInt(cleaned, 10);
              if (!Number.isNaN(n) && n >= 0) {
                setAssignedHours(project.id, row.employeeId, n);
              }
            }}
          />
        </div>
      ),
      getTitleText: (row) => formatHoursWhole(row.assignedHours),
    },
    {
      id: "remainingHours",
      header: "Remaining",
      sortable: true,
      getSortValue: (row) => Math.round(row.remainingHours),
      render: (row) => (
        <span className="body-sm">{formatHoursWhole(row.remainingHours)}</span>
      ),
      getTitleText: (row) => formatHoursWhole(row.remainingHours),
    },
    {
      id: "actions",
      header: "",
      nowrap: true,
      render: (row) => (
        <Button
          type="button"
          variant="accent-ghost"
          onClick={() => openViewAllocations(project.id, row.employeeId)}
        >
          View allocations
        </Button>
      ),
    },
  ];
}

export type EmployeeAllocationTableProps = {
  project: ProjectWithAllocationsDerived;
  setAssignedHours: (projectId: string, employeeId: string, hours: number) => void;
  openViewAllocations: (projectId: string, employeeId: string) => void;
  /** When set, desktop table body scrolls and thead stays sticky. */
  scrollContainerMaxHeight?: string;
};

export function EmployeeAllocationTable({
  project,
  setAssignedHours,
  openViewAllocations,
  scrollContainerMaxHeight,
}: EmployeeAllocationTableProps) {
  const { options: designationOptions } = useProjectDesignationOptions();
  const data: EmployeeTableRow[] = useMemo(
    () =>
      project.employees.map((e) => ({
        projectId: project.id,
        id: e.id,
        employeeId: e.employeeId,
        name: e.name,
        designation: e.designation,
        alreadyAllocatedHours: e.alreadyAllocated / 60,
        apiRemainingHours: e.apiRemainingHours,
        assignedHours: e.assignedHours,
        remainingHours: e.remainingHours,
      })),
    [project],
  );

  const columns = useMemo(
    () => buildColumns(project, setAssignedHours, openViewAllocations),
    [project, setAssignedHours, openViewAllocations, designationOptions],
  );

  return (
    <ResponsiveDataTable<EmployeeTableRow>
      columns={columns}
      data={data}
      pagination={false}
      scrollContainerMaxHeight={scrollContainerMaxHeight}
      aria-label={`Employees for ${project.name}`}
      emptyState={
        <p className="body-sm" style={{ color: "var(--content-muted)" }}>
          No employees assigned.
        </p>
      }
    />
  );
}
